import React from 'react';
import { Box, Divider, SxProps, Tooltip, Typography } from '@mui/material';
import { getMessageAttachmentDownloadLink, Message, MessageAttachment } from '@uplift/api';
import { useOnScreenObserver } from '../../hooks';
import { Color } from '../types';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { AttachmentItem } from './Messages.component';
import { downloadFile } from '@uplift/utils';
import { useAlert } from '../../AlertProvider';
import { MoreVert } from '@mui/icons-material';
import { ClosableMenu, ClosableMenuItem, ClosableMenuProps } from '../../lib/simple-menu';

export type MessagingBubbleDirection = 'receive' | 'send';


export const MessageAttachmentsList: React.FC<{ attachments: MessageAttachment[]; downloadableAttachments?: boolean }> = (props) => {
  const alert = useAlert();

  return (
    <Box sx={{ bgcolor: 'white', color: 'grey.800', maxWidth: '300px', borderRadius: '4px', overflow: 'hidden' }} data-testid="message-attachments">
      {props.attachments.map((attachment, index) => (
        <React.Fragment key={attachment.id}>
          {!!index && <Divider />}
          <AttachmentItem
            key={attachment.id}
            name={attachment.name}
            onClick={props.downloadableAttachments ? async () => {
              try {
                const response = await getMessageAttachmentDownloadLink(attachment.id);

                await downloadFile(response.data.downloadLink, attachment.name);
              } catch (_err) {
                alert.error('Unable to download document');
              }
            } : undefined}
          />
        </React.Fragment>
      ))}
    </Box>
  );
};


export const MessageBubbleActionItem: React.FC<{ onClick: () => void }> = (props) => {
  return (
    <ClosableMenuItem
      dense
      onClick={props.onClick}
      sx={{ color: 'text.primary' }}
    >
      {props.children}
    </ClosableMenuItem>
  );
};


export const MessageBubbleActions: React.FC<{
  placement?: 'start' | 'end';
}> = (props) => {
  const [ open, setOpen ] = React.useState(false);
  const anchorElRef = React.useRef<Element>(null);

  const placementMenuProps: Pick<ClosableMenuProps, 'anchorOrigin' | 'transformOrigin'> = React.useMemo(() => {
    if (props.placement === 'end') {
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      };
    }
    return {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      }
    };
  }, [ props.placement ]);

  return (
    <>
      <Box
        ref={anchorElRef}
        sx={{
          fontSize: 0,
          cursor: 'pointer',
        }}
        onClick={() => setOpen(true)}
      >
        <MoreVert fontSize="small" color="action" />
      </Box>
      <ClosableMenu
        disablePortal
        open={open}
        anchorEl={anchorElRef.current}
        onClose={() => setOpen(false)}
        {...placementMenuProps}
      >
        {props.children}
      </ClosableMenu>
    </>
  );
};


export const MessageBubble: React.VFC<{
  message: Message;
  direction: MessagingBubbleDirection;
  color: Color;
  downloadableAttachments?: boolean;
  onAppearOnScreen?: () => void;
  onDisappearOnScreen?: () => void;

  actions?: React.ReactNode;
}> = (props) => {
  const bubbleStyles = React.useMemo<SxProps>(() => {
    if (props.direction === 'send') {
      return {
        bgcolor: `${props.color}.main`,
        color: 'white',
        borderRadius: '8px 8px 0px 8px',
      };
    } else {
      return {
        bgcolor: `${props.color}.background`,
        borderRadius: '8px 8px 8px 0px',
      };
    }
  }, [ props.direction, props.color ]);

  return (
    <Box
      sx={{
        p: 1,
        width: 'fit-content',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        gap: 1,
        position: 'relative',
        ...bubbleStyles,
      }}
    >
      {props.message.message}

      {!!props.message.attachments?.length && (
        <MessageAttachmentsList
          attachments={props.message.attachments}
          downloadableAttachments={props.downloadableAttachments}
        />
      )}
    </Box>
  );
};


const MessageTime: React.FC<{
  message: Message;
  placement?: 'start' | 'end';
  actions?: React.ReactNode;
}> = (props) => {
  const timezone = useTimezone();
  const messageTime = React.useMemo(() => {
    if (!props.message?.sentAt) return {};

    const date = CalendarDateService.date(props.message.sentAt).tz(timezone);
    const formattedDate = date.format(DateFormats['dddd, MMMM D, YYYY']);
    const formattedTime = date.format(DateFormats['hh:mm A']);

    return {
      time: formattedTime,
      date: formattedDate,
      datetime: formattedTime + ', ' + formattedDate,
    };
  }, [ props.message?.sentAt, timezone ]);

  return (
    <Tooltip title={messageTime.datetime} arrow>
      <Typography
        sx={{
          pl: props.placement === 'end' ? 1 : 0,
          pr: props.placement === 'start' ? 1 : 0,
          userSelect: 'none',
          color: 'grey.400',
        }}
        component="span"
        variant="body2"
        data-testid="message-time"
        whiteSpace="nowrap"
      >
        {messageTime.time}
      </Typography>
    </Tooltip>
  );
};


export const MessageItem: React.VFC<{
  message: Message;
  direction: MessagingBubbleDirection;
  color: Color;
  downloadableAttachments?: boolean;
  onAppearOnScreen?: () => void;
  onDisappearOnScreen?: () => void;

  actions?: React.ReactNode;
}> = (props) => {
  const screenObserverRef = useOnScreenObserver(() => {
    props.onAppearOnScreen?.();
  }, () => {
    props.onDisappearOnScreen?.();
  });

  const bubbleWrapperStyles = React.useMemo<SxProps>(() => {
    if (props.direction === 'send') {
      return {
        justifyContent: 'flex-end',
      };
    } else {
      return {
        justifyContent: 'flex-start',
      };
    }
  }, [ props.direction ]);

  return (
    <Box
      ref={screenObserverRef}
      data-message-id={props.message.id}
      data-testid="message"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        py: 0.75,
        px: 3,
        bgcolor: props.message.seenAt ? 'transparent' : 'grey.50',
        ...bubbleWrapperStyles,
      }}
    >
      {props.direction === 'send' && (
        <MessageTime
          message={props.message}
          placement="start"
          actions={props.actions}
        />
      )}
      {props.direction === 'receive' && props.actions && (
        <MessageBubbleActions placement="start">
          {props.actions}
        </MessageBubbleActions>
      )}
      <MessageBubble {...props} />
      {props.direction === 'send' && props.actions && (
        <MessageBubbleActions placement="end">
          {props.actions}
        </MessageBubbleActions>
      )}
      {props.direction === 'receive' && (
        <MessageTime
          message={props.message}
          placement="end"
          actions={props.actions}
        />
      )}
    </Box>
  );
};
